import { useEffect, useState } from "react";
import { isAfter, isEqual } from "date-fns";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { calculateAbsoluteDateFromRelativeConfig } from "../../../common/relativeDateCalculation.ts";
import { SelectedObligationTemplate } from "../../addContractButton/createContractPane/CreateContractPane.types.ts";
import { ObligationValidationErrors } from "./ObligationTemplatesList.types.ts";
import { SelectableObligationTemplates } from "../../../entityTemplates/EntityTemplate.types.ts";
import { RelativeDateReference } from "../../../annualWheel/RelativeDate.types.ts";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery.ts";

export interface UseObligationTemplatesListProps {
    obligationTemplates: SelectableObligationTemplates[];
    initialSelectedObligations?: SelectedObligationTemplate[];
    contractStartDate?: Date | null;
    contractEndDate?: Date | null;
    showErrors: boolean;
    onChange: (obligations: SelectedObligationTemplate[]) => void;
    onValidationChange?: (hasErrors: boolean) => void;
}

export function useObligationTemplatesList(props: UseObligationTemplatesListProps) {
    const { translateString } = useTranslation();
    const selectableUsersQuery = useResponsibleQuery();
    const [selectedObligations, setSelectedObligations] = useState<SelectedObligationTemplate[]>(props.initialSelectedObligations ?? []);
    const { onChange, onValidationChange } = props;

    useEffect(() => {
        onChange(selectedObligations);
    }, [selectedObligations, onChange]);

    useEffect(() => {
        setSelectedObligations((prev) => {
            const mandatoryTemplates = props.obligationTemplates.filter((t) => t.isMandatory && !t.isUsed);
            const missingMandatory = mandatoryTemplates.filter((t) => !prev.some((o) => o.obligationTemplateId === t.id));
            if (missingMandatory.length === 0) {
                return prev;
            }

            const newObligations = missingMandatory.map((template) => ({
                obligationTemplateId: template.id,
                startDate: calculateAbsoluteDateFromRelativeConfig(template.startDate, props.contractStartDate, props.contractEndDate),
                endDate: calculateAbsoluteDateFromRelativeConfig(template.endDate, props.contractStartDate, props.contractEndDate),
            }));
            return [...prev, ...newObligations];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.obligationTemplates]);

    useEffect(() => {
        setSelectedObligations((prev) =>
            prev.map((obligation) => {
                const template = props.obligationTemplates.find((t) => t.id === obligation.obligationTemplateId);
                if (!template || !template.containsRelativeDate) {
                    return obligation;
                }

                const hasRelativeStartDate = template.startDate?.value != null;
                const hasRelativeEndDate = template.endDate?.value != null;

                return {
                    ...obligation,
                    startDate: hasRelativeStartDate
                        ? calculateAbsoluteDateFromRelativeConfig(template.startDate, props.contractStartDate, props.contractEndDate)
                        : obligation.startDate,
                    endDate: hasRelativeEndDate
                        ? calculateAbsoluteDateFromRelativeConfig(template.endDate, props.contractStartDate, props.contractEndDate)
                        : obligation.endDate,
                };
            })
        );
    }, [props.contractStartDate, props.contractEndDate, props.obligationTemplates]);

    const toggleSelection = (obligationTemplateId: string) => {
        const template = props.obligationTemplates.find((t) => t.id === obligationTemplateId);
        const isAlreadySelected = selectedObligations.some((o) => o.obligationTemplateId === obligationTemplateId);

        if (template?.isMandatory && isAlreadySelected && !template.isUsed) {
            return;
        }

        setSelectedObligations((prev) => {
            const exists = prev.some((o) => o.obligationTemplateId === obligationTemplateId);
            if (exists) {
                return prev.filter((o) => o.obligationTemplateId !== obligationTemplateId);
            }

            const calculatedStartDate = template
                ? calculateAbsoluteDateFromRelativeConfig(template.startDate, props.contractStartDate, props.contractEndDate)
                : null;
            const calculatedEndDate = template
                ? calculateAbsoluteDateFromRelativeConfig(template.endDate, props.contractStartDate, props.contractEndDate)
                : null;

            return [...prev, { obligationTemplateId, startDate: calculatedStartDate, endDate: calculatedEndDate }];
        });
    };

    const updateStartDate = (obligationTemplateId: string, startDate: Date | null) => {
        setSelectedObligations((prev) => {
            const exists = prev.some((o) => o.obligationTemplateId === obligationTemplateId);
            if (!exists) {
                return [...prev, { obligationTemplateId, startDate }];
            }
            return prev.map((o) => (o.obligationTemplateId === obligationTemplateId ? { ...o, startDate } : o));
        });
    };

    const updateEndDate = (obligationTemplateId: string, endDate: Date | null) => {
        setSelectedObligations((prev) => {
            const exists = prev.some((o) => o.obligationTemplateId === obligationTemplateId);
            if (!exists) {
                return [...prev, { obligationTemplateId, endDate }];
            }
            return prev.map((o) => (o.obligationTemplateId === obligationTemplateId ? { ...o, endDate } : o));
        });
    };

    const updateResponsible = (obligationTemplateId: string, responsibleId: string | null) => {
        setSelectedObligations((prev) => prev.map((o) => (o.obligationTemplateId === obligationTemplateId ? { ...o, responsibleId } : o)));
    };

    const getSelectedObligation = (obligationTemplateId: string) => {
        return selectedObligations.find((o) => o.obligationTemplateId === obligationTemplateId);
    };

    const isSelected = (obligationTemplateId: string) => {
        return selectedObligations.some((o) => o.obligationTemplateId === obligationTemplateId);
    };

    const validationErrors = new Map<string, ObligationValidationErrors>();
    selectedObligations.forEach((obligation) => {
        const obligationErrors: ObligationValidationErrors = {};
        const template = props.obligationTemplates.find((t) => t.id === obligation.obligationTemplateId);

        if (!obligation.startDate) {
            obligationErrors.startDate = translateString("validateNotEmpty", { name: translateString("startDate") });
        }

        if (!template?.isCarriedOutOnce && !obligation.endDate) {
            obligationErrors.endDate = translateString("validateNotEmpty", { name: translateString("endDate") });
        }

        if (obligation.startDate && obligation.endDate) {
            if (!isEqual(obligation.endDate, obligation.startDate) && !isAfter(obligation.endDate, obligation.startDate)) {
                obligationErrors.endDate = translateString("contractInvalidDateRange", {
                    startLabel: translateString("startDate"),
                    endLabel: translateString("endDate"),
                });
            }
        }

        if (!obligation.responsibleId) {
            obligationErrors.responsible = translateString("validateNotEmpty", { name: translateString("responsible") });
        }

        if (obligationErrors.startDate || obligationErrors.endDate || obligationErrors.responsible) {
            validationErrors.set(obligation.obligationTemplateId, obligationErrors);
        }
    });

    const selectedObligationNeedsContractStartDate = selectedObligations.some((obligation) => {
        const template = props.obligationTemplates.find((t) => t.id === obligation.obligationTemplateId);
        if (!template) return false;
        return (
            (template.startDate?.value != null && template.startDate.relativeTo === RelativeDateReference.ContractStart) ||
            (template.endDate?.value != null && template.endDate.relativeTo === RelativeDateReference.ContractStart)
        );
    });

    const selectedObligationNeedsContractEndDate = selectedObligations.some((obligation) => {
        const template = props.obligationTemplates.find((t) => t.id === obligation.obligationTemplateId);
        if (!template) return false;
        return (
            (template.startDate?.value != null && template.startDate.relativeTo === RelativeDateReference.ContractEnd) ||
            (template.endDate?.value != null && template.endDate.relativeTo === RelativeDateReference.ContractEnd)
        );
    });

    const contractStartDateError =
        selectedObligationNeedsContractStartDate && !props.contractStartDate
            ? translateString("dateNeededToCalculateObligationDates", { name: translateString("startDate") })
            : undefined;

    const contractEndDateError =
        selectedObligationNeedsContractEndDate && !props.contractEndDate
            ? translateString("dateNeededToCalculateObligationDates", { name: translateString("endDate") })
            : undefined;

    const hasValidationErrors = validationErrors.size > 0 || !!contractStartDateError || !!contractEndDateError;

    useEffect(() => {
        onValidationChange?.(hasValidationErrors);
    }, [hasValidationErrors, onValidationChange]);

    return {
        selectableUsersQuery,
        validationErrors,
        contractStartDateError,
        contractEndDateError,
        toggleSelection,
        updateStartDate,
        updateEndDate,
        updateResponsible,
        getSelectedObligation,
        isSelected,
    };
}
